<template>
  <div class="container">
    <p>Faced with such a challenge, a curious mind will want to try...</p>

    <div class="action-grid">
      <div class="code-button noSelect" id="click-or-tap">
        <div id="tap-finger-1" class="tap-finger"></div>
        1
      </div>
      <div class="action-text">clicking/tapping</div>
      <div class="code-button noSelect" id="tap-and-hold">
        <svg
          style="position: absolute"
          height="100%"
          width="100%"
          viewBox="0 0 100 100"
        >
          <circle
            id="tap-and-hold-circle"
            cx="50"
            cy="50"
            r="45"
            stroke="#FF3633"
            fill="none"
            stroke-width="3"
            :stroke-linecap="tapAndHoldTheta > 0 ? 'round' : 'butt'"
            :style="tapAndHoldCircleStyle"
          />
        </svg>
        <div id="tap-finger-2" class="tap-finger"></div>
        1
      </div>
      <div class="action-text">clicking/tapping and holding</div>
      <div class="code-button noSelect" id="hold-and-drag">
        <div id="tap-finger-3" class="tap-finger"></div>
        1
      </div>
      <div class="action-text">dragging</div>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap'
import lottie from 'lottie-web'

import * as tapAnimationData from '@/assets/tap_finger.json'

export default {
  name: 'Tutorial2',
  data() {
    return {
      tapAndHoldTheta: 0,
      tapAnim: undefined,
    }
  },
  mounted() {
    this.setupTapAnimation()
    this.setupTapAndHoldAnimation()
    this.setupHoldAndDragAnimation()
  },
  unmount() {
    console.log('unmounted')
    this.tapAnim.destroy()
  },
  computed: {
    tapAndHoldCircleStyle() {
      const r = 45
      const circum = 2 * Math.PI * r
      const arcLength = this.tapAndHoldTheta * (circum / (2 * Math.PI))
      return {
        strokeDasharray: `${arcLength} ${circum - arcLength}`,
        strokeDashoffset: `${circum / 4}`,
      }
    },
  },
  methods: {
    setupTapAnimation() {
      const anim = lottie.loadAnimation({
        container: document.getElementById('tap-finger-1'),
        animationData: tapAnimationData.default,
        renderer: 'svg',
        autoplay: false,
        loop: false,
      })

      const tapAnim = () => {
        console.log('tapAnim')
        if (!document.getElementById('click-or-tap')) {
          console.log('cant find click-or-tap element')
          return
        }
        setTimeout(() => {
          anim.goToAndPlay(0)
          setTimeout(() => {
            gsap.fromTo(
              document.getElementById('click-or-tap'),
              {
                delay: 0.5,
                backgroundColor: 'rgba(205,244,221,0.3)',
              },
              {
                backgroundColor: 'transparent',
                duration: 0.5,
              }
            )
          }, 350)
        }, 1000)
      }

      anim.addEventListener('complete', tapAnim)
      tapAnim()
      this.tapAnim = tapAnim
    },
    setupTapAndHoldAnimation() {
      const fingerAnim = lottie.loadAnimation({
        container: document.getElementById('tap-finger-2'),
        animationData: tapAnimationData.default,
        renderer: 'svg',
        autoplay: false,
        loop: false,
      })

      const checkAndKill = () => {
        if (!document.getElementById('tap-and-hold')) {
          console.log('cant find tap-and-hold element')
          tl.kill()
          return
        }
      }
      const tl = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onStart: checkAndKill,
        onRepeat: checkAndKill,
      })

      tl.fromTo(
        this,
        { tapAndHoldTheta: 0 },
        {
          tapAndHoldTheta: 0,
          duration: 1,
          onComplete: () => {
            fingerAnim.playSegments([0, 17], true)
            setTimeout(() => {
              gsap.fromTo(
                document.getElementById('tap-and-hold'),
                {
                  backgroundColor: 'rgba(205,244,221,0.3)',
                },
                {
                  backgroundColor: 'transparent',
                  duration: 0.5,
                }
              )
            }, 400)
          },
        }
      )

      tl.fromTo(
        this,
        {
          tapAndHoldTheta: 0,
        },
        {
          tapAndHoldTheta: 2 * Math.PI,
          duration: 2,
          ease: 'linear',
          delay: 0.4,
          onComplete: () => {
            fingerAnim.playSegments([17, 30], true)
          },
        },
        'hold'
      )

      tl.fromTo(
        document.getElementById('tap-and-hold-circle'),
        { opacity: 1 },
        { opacity: 0, duration: 0.5, delay: 0.2 }
      )

      tl.set(this, { tapAndHoldTheta: 0 })
    },
    setupHoldAndDragAnimation() {
      const fingerAnim = lottie.loadAnimation({
        container: document.getElementById('tap-finger-3'),
        animationData: tapAnimationData.default,
        renderer: 'svg',
        autoplay: false,
        loop: false,
      })

      const checkAndKill = () => {
        if (!document.getElementById('hold-and-drag')) {
          console.log('cant find hold-and-drag element')
          tl.kill()
        }
      }

      const tl = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onStart: checkAndKill,
        onRepeat: checkAndKill,
      })

      tl.fromTo(
        document.getElementById('hold-and-drag'),
        { x: 0, opacity: 1 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          onComplete: () => {
            fingerAnim.playSegments([0, 17], true)
            setTimeout(() => {
              gsap.fromTo(
                document.getElementById('hold-and-drag'),
                {
                  backgroundColor: 'rgba(205,244,221,0.3)',
                },
                {
                  backgroundColor: 'transparent',
                  duration: 0.5,
                }
              )
            }, 400)
          },
        }
      )

      tl.fromTo(
        document.getElementById('hold-and-drag'),
        { x: '0%' },
        {
          x: '50%',
          duration: 1,
          ease: 'linear',
          delay: 0.4,
          onComplete: () => {
            fingerAnim.playSegments([17, 30], true)
          },
        }
      )
      tl.fromTo(
        document.getElementById('hold-and-drag'),
        { opacity: 1 },
        { opacity: 0, duration: 0.3, delay: 0.8 }
      )
      tl.fromTo(
        document.getElementById('hold-and-drag'),
        { x: 0, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.3 }
      )
    },
  },
}
</script>

<style>
.tap-finger path {
  fill-opacity: 1;
  fill: #003745;
}
</style>

<style scoped>
.container {
  height: 100%;
  width: 100%;
  color: #cdf4dd;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  box-sizing: border-box;
}

p {
  margin-top: 0px;
  margin-bottom: 2em;
}

.code-container {
  color: #cdf4dd;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: calc(clamp(0.25rem, 3vmin, 0.75rem) / 1.5);
  row-gap: calc(clamp(0.25rem, 3vmin, 0.75rem) / 1.5);
}

.action-grid {
  display: grid;
  /*grid-template-columns: 0.8fr 1fr;*/
  grid-template-columns:
    calc(2 * clamp(2rem, 17.6vmin, 4.5rem) / 1.5)
    1fr;
  column-gap: 1rem;
  row-gap: 2rem;
}

.action-text {
  display: flex;
  align-items: center;
  text-align: left;
}

.code-button {
  position: relative;
  width: calc(clamp(2rem, 17.6vmin, 4.5rem) / 1.5);
  height: calc(clamp(2rem, 17.6vmin, 4.5rem) / 1.5);
  border-radius: 50%;
  border: 0.5px solid #cdf4dd;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: calc(clamp(1rem, 8.8vmin, 2.5rem) / 1.5);
  font-family: 'Open Sans';
  font-weight: 100;
  cursor: pointer;
  user-select: none;
}

.tap-finger {
  position: absolute;
  width: 200%;
  height: 200%;
  left: -25%;
  top: -25%;
}

.noSelect {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.click-effect {
  animation: click-effect-animation 0.5s ease-out infinite;
}

@keyframes click-effect-animation {
  0% {
    background-color: rgba(205, 244, 221, 0.3);
  }
  100% {
    background-color: transparent;
  }
}
</style>
